import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { dubaiDaysSince } from '../../../utils/dubaiTime';
import './clients.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';

function Clients() {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Zoho pending summary keyed by zoho_contact_id (loaded separately, non-blocking)
  const [pendingSummary, setPendingSummary] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Wallet balances keyed by client_id — the separate /wallet page folded into
  // this one (owner, 2026-09-02). Non-blocking like the Zoho summary.
  const [wallets, setWallets] = useState({});
  const [walletsLoading, setWalletsLoading] = useState(true);

  // Column sorting
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });

  // ✅ Collapsible archived section (default closed)
  const [archivedOpen, setArchivedOpen] = useState(() => {
    // optional: remember state between refreshes
    const saved = localStorage.getItem('clients_archived_open');
    return saved ? saved === '1' : false;
  });

  const navigate = useNavigate();
  // Aggregate money is management information: the totals row is manager-only,
  // exactly as it was on the retired /wallet page.
  const isManager = (localStorage.getItem('userRole') || '').toLowerCase() === 'manager';

  // Separate entity wallets: the list shows ONE combined figure per client
  // (owner 2026-09-23: "combined wallet balance") — the main wallet plus every
  // entity wallet that belongs to it — with the split on hover.
  const combinedWallet = useCallback((clientId) => {
    const main = wallets[clientId];
    const entities = Object.values(wallets).filter((w) => Number(w.wallet_parent_id) === Number(clientId));
    if (!main && entities.length === 0) return null;
    const sum = (k) => Number(main?.[k] || 0) + entities.reduce((a, w) => a + Number(w[k] || 0), 0);
    return {
      cash_balance: sum('cash_balance'), bonus_balance: sum('bonus_balance'), total_balance: sum('total_balance'),
      parts: entities.length
        ? [{ label: 'Main wallet', total: Number(main?.total_balance || 0) },
           ...entities.map((w) => ({ label: `${w.entity_label || w.display_name} wallet`, total: Number(w.total_balance || 0) }))]
        : null,
    };
  }, [wallets]);

  const walletTotals = useMemo(() => Object.values(wallets).reduce(
    (acc, w) => ({
      cash: acc.cash + Number(w.cash_balance || 0),
      bonus: acc.bonus + Number(w.bonus_balance || 0),
      total: acc.total + Number(w.total_balance || 0),
    }), { cash: 0, bonus: 0, total: 0 }), [wallets]);

  const pendingGrandTotal = useMemo(() => Object.values(pendingSummary).reduce(
    (sum, sVal) => sum + Number(sVal?.pending_total || 0), 0), [pendingSummary]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axios.get('/api/clients');
        if (response.status === 200) {
          const sortedClients = response.data.sort((a, b) => a.id - b.id);
          setClients(sortedClients);
          setLoading(false);
        } else {
          throw new Error('Error fetching clients');
        }
      } catch (error) {
        setError('Error fetching clients. Please try again later.');
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  // Fetch the org-wide Zoho pending summary once (cheap, cached server-side).
  // The table renders immediately; these columns fill in when this resolves.
  useEffect(() => {
    const fetchPendingSummary = async () => {
      setSummaryLoading(true);
      try {
        const response = await axios.get('/api/clients/zoho-pending-summary');
        setPendingSummary(response.data?.summary || {});
      } catch (error) {
        console.error('Error fetching pending summary:', error);
        setPendingSummary({});
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchPendingSummary();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/api/wallet/clients');
        const map = {};
        (res.data || []).forEach((w) => { map[w.client_id] = w; });
        setWallets(map);
      } catch (e) {
        console.error('Error fetching wallet balances:', e);
        setWallets({});
      } finally {
        setWalletsLoading(false);
      }
    })();
  }, []);

  const filteredClients = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return clients.filter((client) => {
      const fullName = `${client.first_name || ''} ${client.last_name || ''}`.toLowerCase();
      const searchFields = [
        (client.display_name || '').toLowerCase(),
        (client.company_name || '').toLowerCase(),
        (client.first_name || '').toLowerCase(),
        (client.last_name || '').toLowerCase(),
        fullName,
        String(client.id || ''),
      ];

      return searchFields.some((field) => field.includes(term));
    });
  }, [clients, searchTerm]);

  const formatId = (id) => String(id).padStart(5, '0');

  const money = (n) =>
    `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const PAYMENT_TERMS_DAYS = 7; // standard terms used when an invoice has no due date

  // Whole days an invoice is overdue, based on its due date (or invoice date +
  // standard terms if no due date). Positive = overdue, negative = not yet due.
  // ITEM 8: both 'today' and the due day are ASIA/DUBAI calendar days via the
  // shared util — the old new Date(...) math used DEVICE-local day boundaries,
  // so overdue counts shifted on any non-Dubai machine. Zoho date-only strings
  // ('yyyy-MM-dd') are calendar days and stay that Dubai day, never shifted
  // through UTC.
  const daysOverdue = (inv) => {
    if (inv?.due_date) return dubaiDaysSince(inv.due_date);
    if (inv?.date) return dubaiDaysSince(inv.date, PAYMENT_TERMS_DAYS);
    return null;
  };

  // Value used for sorting a client by a given column key. Returns null for
  // "unknown" values so they always sort to the bottom regardless of direction.
  const getSortValue = (client, key) => {
    const summary = pendingSummary[client.zoho_contact_id] || null;
    switch (key) {
      case 'id':
        return client.id;
      case 'display_name':
        return (client.display_name || '').toLowerCase();
      case 'wallet_balance': {
        const w = combinedWallet(client.id);
        return w ? Number(w.total_balance || 0) : null;
      }
      case 'property_count':
        return Number(client.property_count || 0);
      case 'pending_total':
        return summary ? Number(summary.pending_total || 0) : null;
      case 'oldest_invoice': {
        const inv = summary?.oldest_unpaid_invoice;
        return inv ? daysOverdue(inv) : null;
      }
      default:
        return client.id;
    }
  };

  const sortList = (list) => {
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;

    return [...list].sort((a, b) => {
      const va = getSortValue(a, key);
      const vb = getSortValue(b, key);

      const aNull = va === null || va === undefined || va === '';
      const bNull = vb === null || vb === undefined || vb === '';
      if (aNull && bNull) return 0;
      if (aNull) return 1; // nulls always last
      if (bNull) return -1;

      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  };

  const activeClients = useMemo(
    () => sortList(filteredClients.filter((client) => client.is_active !== false)),
    // sortList is re-created every render but only reads sortConfig/pendingSummary,
    // which ARE dependencies — listing the function itself would defeat the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredClients, sortConfig, pendingSummary]
  );

  const archivedClients = useMemo(
    () => sortList(filteredClients.filter((client) => client.is_active === false)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredClients, sortConfig, pendingSummary]
  );

  const handleRowClick = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  const requestSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const sortIndicator = (key) => {
    if (sortConfig.key !== key) return ' ⇅';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const toggleArchived = () => {
    setArchivedOpen((prev) => {
      const next = !prev;
      localStorage.setItem('clients_archived_open', next ? '1' : '0');
      return next;
    });
  };

  const renderHeader = () => (
    <thead>
      <tr>
        <th className="cl-sortable" onClick={() => requestSort('id')}>Client ID{sortIndicator('id')}</th>
        {/* ONE name. Company and primary contact left the table (owner,
            2026-09-02) — the search still matches them, and the details page
            still shows them. What replaced the width is the wallet balance,
            which used to need a whole separate page to see. */}
        <th className="cl-sortable" onClick={() => requestSort('display_name')}>Display Name{sortIndicator('display_name')}</th>
        <th className="cl-sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('wallet_balance')}>Wallet Balance{sortIndicator('wallet_balance')}</th>
        <th className="cl-sortable" style={{ textAlign: 'center' }} onClick={() => requestSort('property_count')}>Properties{sortIndicator('property_count')}</th>
        <th className="cl-sortable" style={{ textAlign: 'right' }} onClick={() => requestSort('pending_total')}>Pending Amount{sortIndicator('pending_total')}</th>
        <th className="cl-sortable" onClick={() => requestSort('oldest_invoice')}>Oldest Unpaid Invoice{sortIndicator('oldest_invoice')}</th>
      </tr>
    </thead>
  );

  const renderWalletCell = (client) => {
    const w = combinedWallet(client.id);
    if (!w) {
      return (
        <td style={{ textAlign: 'right' }}>
          {walletsLoading ? <span className="cl-loading-cell">Loading…</span> : money(0)}
        </td>
      );
    }
    const total = Number(w.total_balance || 0);
    const split = w.parts ? w.parts.map((p) => `${p.label}: ${money(p.total)}`).join(' · ') : null;
    return (
      <td style={{ textAlign: 'right', fontWeight: total > 0 ? 700 : 400 }} title={split || undefined}>
        {money(total)}
      </td>
    );
  };

  const renderPendingCell = (client) => {
    if (!client.zoho_contact_id) return <td style={{ textAlign: 'right' }}>—</td>;
    const summary = pendingSummary[client.zoho_contact_id];
    if (!summary) {
      return (
        <td style={{ textAlign: 'right' }}>
          {summaryLoading ? <span className="cl-loading-cell">Loading…</span> : money(0)}
        </td>
      );
    }
    const amount = Number(summary.pending_total || 0);
    // Separate entity wallets: combined figure, the entity's share on hover.
    const pools = Array.isArray(summary.entity_pools) ? summary.entity_pools : [];
    const split = pools.length
      ? [`Main: ${money(amount - pools.reduce((a, p) => a + Number(p.pending_total || 0), 0))}`, ...pools.map((p) => `${p.label}: ${money(Number(p.pending_total || 0))}`)].join(' · ')
      : null;
    return (
      <td style={{ textAlign: 'right' }} className={amount > 0 ? 'cl-pending-due' : ''} title={split || undefined}>
        {money(amount)}
      </td>
    );
  };

  const renderOldestInvoiceCell = (client) => {
    if (!client.zoho_contact_id) return <td>—</td>;
    const summary = pendingSummary[client.zoho_contact_id];
    if (!summary) return <td>{summaryLoading ? <span className="cl-loading-cell">Loading…</span> : '—'}</td>;

    const inv = summary.oldest_unpaid_invoice;
    if (!inv) return <td>—</td>;

    const overdue = daysOverdue(inv);
    let overdueLabel;
    let overdueClass = 'cl-oldest-id';
    if (overdue === null) {
      overdueLabel = '—';
    } else if (overdue > 0) {
      overdueLabel = `${overdue} day${overdue === 1 ? '' : 's'} overdue`;
      overdueClass += ' cl-overdue';
    } else if (overdue === 0) {
      overdueLabel = 'Due today';
    } else {
      overdueLabel = `Due in ${-overdue} day${overdue === -1 ? '' : 's'}`;
    }

    return (
      <td>
        <div className="cl-oldest">
          <span className={overdueClass}>{overdueLabel}</span>
          <span className="cl-oldest-meta">{money(inv.balance)} pending</span>
        </div>
      </td>
    );
  };

  const renderRow = (client, archived) => (
    <tr
      key={client.id}
      className={`client-row${archived ? ' archived' : ''}`}
      onClick={() => handleRowClick(client.id)}
      style={{ cursor: 'pointer' }}
    >
      <td>#{formatId(client.id)}</td>
      <td style={{ fontWeight: 600 }}>{client.display_name}</td>
      {renderWalletCell(client)}
      <td style={{ textAlign: 'center' }}>{client.property_count || 0}</td>
      {renderPendingCell(client)}
      {renderOldestInvoiceCell(client)}
    </tr>
  );

  return (
    <div className="clients-container">
      <h2>Clients</h2>

      <div className="clients-header">
        <div className="search-bar-wrapper">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-bar"
          />
        </div>

        <Link to="/clients/new">
          <button className="add-client-button">+ Add New Client</button>
        </Link>
      </div>

      {isManager && (
        <div className="cl-summaryRow">
          <div className="cl-summaryCard">
            <div className="cl-summaryLabel">Total Cash Balance</div>
            <div className="cl-summaryValue">{walletsLoading ? '…' : money(walletTotals.cash)}</div>
          </div>
          <div className="cl-summaryCard">
            <div className="cl-summaryLabel">Total Bonus Balance</div>
            <div className="cl-summaryValue">{walletsLoading ? '…' : money(walletTotals.bonus)}</div>
          </div>
          <div className="cl-summaryCard cl-summaryCard--accent">
            <div className="cl-summaryLabel">Total Wallet Balance</div>
            <div className="cl-summaryValue">{walletsLoading ? '…' : money(walletTotals.total)}</div>
          </div>
          <div className="cl-summaryCard cl-summaryCard--pending">
            <div className="cl-summaryLabel">Total Pending Invoices</div>
            <div className="cl-summaryValue">{summaryLoading ? '…' : money(pendingGrandTotal)}</div>
          </div>
        </div>
      )}

      {loading && <p>Loading clients...</p>}
      {error && <p className="error-message">{error}</p>}

      {!loading && !error && (
        <>
          {summaryLoading && (
            <div className="clients-pending-loading" role="status" aria-live="polite">
              <span className="clients-pending-spinner" aria-hidden="true" />
              <span>Loading pending balances &amp; invoices from Zoho… this can take a few seconds.</span>
            </div>
          )}

          <table className="clients-table">
            {renderHeader()}
            <tbody>
              {activeClients.map((client) => renderRow(client, false))}
            </tbody>
          </table>

          {/* ✅ Collapsible Archived Clients */}
          {archivedClients.length > 0 && (
            <div className="archived-clients-section">
              <div
                className="archived-clients-header"
                onClick={toggleArchived}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleArchived()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none',
                  marginTop: 18,
                }}
              >
                <h3 style={{ margin: 0 }}>Archived Clients ({archivedClients.length})</h3>
                <FontAwesomeIcon icon={archivedOpen ? faChevronDown : faChevronRight} />
              </div>

              {archivedOpen && (
                <table className="clients-table archived-clients-table" style={{ marginTop: 10 }}>
                  {renderHeader()}
                  <tbody>
                    {archivedClients.map((client) => renderRow(client, true))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Clients;
